import { useState } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { TooltipProvider } from '@/components/ui/tooltip'
import { ChevronLeft, ChevronRight, Download, Search, Plus, Edit3, Check, X, TrendingUp } from 'lucide-react'
import { formatMoeda } from '@/utils/format'

interface CreditoItem {
  id: number
  periodoApurado: string
  credPisOriginal: number
  credPisSaldo: number
  credPisPago: number
  dcompInicialPis: string
  credCofinsOriginal: number
  credCofinsSaldo: number
  credCofinsPago: number
  dcompInicialCofins: string
}

const dadosIniciaisCreditos: CreditoItem[] = [
  {
    id: 1,
    periodoApurado: '01/2024',
    credPisOriginal: 2500.0,
    credPisSaldo: 1250.0,
    credPisPago: 1250.0,
    dcompInicialPis: '18352.53597.150425.1.3.04-8815',
    credCofinsOriginal: 7500.0,
    credCofinsSaldo: 3750.0,
    credCofinsPago: 3750.0,
    dcompInicialCofins: '00746.77076.150425.1.3.04-0860'
  },
  {
    id: 2,
    periodoApurado: '02/2024',
    credPisOriginal: 1960.0,
    credPisSaldo: 980.0,
    credPisPago: 980.0,
    dcompInicialPis: '13245.74588.150425.1.3.04-9000',
    credCofinsOriginal: 5880.0,
    credCofinsSaldo: 2940.0,
    credCofinsPago: 2940.0,
    dcompInicialCofins: '06003.01133.150425.1.3.04-3940'
  },
  {
    id: 3,
    periodoApurado: '03/2024',
    credPisOriginal: 3120.0,
    credPisSaldo: 1560.0,
    credPisPago: 1560.0,
    dcompInicialPis: '36175.96655.150425.1.3.04-4220',
    credCofinsOriginal: 9360.0,
    credCofinsSaldo: 4680.0,
    credCofinsPago: 4680.0,
    dcompInicialCofins: '35282.66527.150425.1.3.04-4614'
  },
  {
    id: 4,
    periodoApurado: '04/2024',
    credPisOriginal: 4200.0,
    credPisSaldo: 2100.0,
    credPisPago: 2100.0,
    dcompInicialPis: '23191.05299.150425.1.3.04-3441',
    credCofinsOriginal: 12600.0,
    credCofinsSaldo: 6300.0,
    credCofinsPago: 6300.0,
    dcompInicialCofins: '18352.53597.150425.1.3.04-8815'
  },
  {
    id: 5,
    periodoApurado: '05/2024',
    credPisOriginal: 1500.0,
    credPisSaldo: 750.0,
    credPisPago: 750.0,
    dcompInicialPis: '00746.77076.150425.1.3.04-0860',
    credCofinsOriginal: 4500.0,
    credCofinsSaldo: 2250.0,
    credCofinsPago: 2250.0,
    dcompInicialCofins: '13245.74588.150425.1.3.04-9000'
  },
  {
    id: 6,
    periodoApurado: '06/2024',
    credPisOriginal: 3600.0,
    credPisSaldo: 1800.0,
    credPisPago: 1800.0,
    dcompInicialPis: '06003.01133.150425.1.3.04-3940',
    credCofinsOriginal: 10800.0,
    credCofinsSaldo: 5400.0,
    credCofinsPago: 5400.0,
    dcompInicialCofins: '36175.96655.150425.1.3.04-4220'
  }
]

export default function PlanilhaControleCreditos() {
  const [dados, setDados] = useState(dadosIniciaisCreditos)
  const [busca, setBusca] = useState('')
  const [paginaAtual, setPaginaAtual] = useState(1)
  const [celulasEditando, setCelulasEditando] = useState<Set<string>>(new Set())
  const [valoresTemp, setValoresTemp] = useState<Record<string, any>>({})
  const itensPorPagina = 5


  // const calcularPercentualUtilizado = (utilizado: number, original: number) => {
  //   return Math.round((utilizado / original) * 100)
  // }

  const iniciarEdicao = (itemId: number, campo: string) => {
    const chave = `${itemId}-${campo}`
    setCelulasEditando(prev => new Set([...prev, chave]))
    const item = dados.find(d => d.id === itemId)
    if (item) {
      setValoresTemp(prev => ({ ...prev, [chave]: item[campo as keyof CreditoItem] }))
    }
  }

  const salvarEdicao = (itemId: number, campo: string) => {
    const chave = `${itemId}-${campo}`
    const novoValor = valoresTemp[chave]

    setDados(prev =>
      prev.map(item =>
        item.id === itemId
          ? { ...item, [campo]: campo.includes('Utilizado') ? Number.parseFloat(novoValor) || 0 : novoValor }
          : item
      )
    )

    setCelulasEditando(prev => {
      const nova = new Set(prev)
      nova.delete(chave)
      return nova
    })

    setValoresTemp(prev => {
      const novo = { ...prev }
      delete novo[chave]
      return novo
    })
  }

  const cancelarEdicao = (itemId: number, campo: string) => {
    const chave = `${itemId}-${campo}`
    setCelulasEditando(prev => {
      const nova = new Set(prev)
      nova.delete(chave)
      return nova
    })
    setValoresTemp(prev => {
      const novo = { ...prev }
      delete novo[chave]
      return novo
    })
  }

  const atualizarValorTemp = (itemId: number, campo: string, valor: any) => {
    const chave = `${itemId}-${campo}`
    setValoresTemp(prev => ({ ...prev, [chave]: valor }))
  }

  // Filtragem de dados baseada na busca
  const dadosFiltrados = dados.filter(
    item =>
      item.periodoApurado.includes(busca) ||
      item.dcompInicialPis.includes(busca) ||
      item.dcompInicialCofins.includes(busca)
  )

  // Paginação
  const totalPaginas = Math.ceil(dadosFiltrados.length / itensPorPagina)
  const indiceInicial = (paginaAtual - 1) * itensPorPagina
  const indiceFinal = indiceInicial + itensPorPagina
  const dadosPaginados = dadosFiltrados.slice(indiceInicial, indiceFinal)

  // Cálculos para informações externas
  // const totalPisOriginal = dados.reduce((acc, item) => acc + item.credPisOriginal, 0)
  // const totalPisUtilizado = dados.reduce((acc, item) => acc + item.credPisSaldo, 0)
  // const totalCofinsOriginal = dados.reduce((acc, item) => acc + item.credCofinsOriginal, 0)
  // const totalCofinsUtilizado = dados.reduce((acc, item) => acc + item.credCofinsSaldo, 0)

  // const pisRestanteTotal = totalPisOriginal - totalPisUtilizado
  // const cofinsRestanteTotal = totalCofinsOriginal - totalCofinsUtilizado
  // const percentualPisGeral = calcularPercentualUtilizado(totalPisUtilizado, totalPisOriginal)
  // const percentualCofinsGeral = calcularPercentualUtilizado(totalCofinsUtilizado, totalCofinsOriginal)

  const CelulaEditavel = ({
    itemId,
    campo,
    valor,
    tipo = 'text',
    formatarValor = (v: any) => v
  }: {
    itemId: number
    campo: string
    valor: any
    tipo?: string
    formatarValor?: (v: any) => string
  }) => {
    const chave = `${itemId}-${campo}`
    const estaEditando = celulasEditando.has(chave)
    const valorTemp = valoresTemp[chave]

    if (estaEditando) {
      return (
        <div className="flex flex-col gap-1">
          <Input
            type={tipo}
            value={valorTemp}
            onChange={e => atualizarValorTemp(itemId, campo, e.target.value)}
            className="h-8 text-sm"
            step={tipo === 'number' ? '0.01' : undefined}
            autoFocus
          />
          <div className="flex justify-end gap-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 text-green-600 hover:text-green-700"
              onClick={() => salvarEdicao(itemId, campo)}
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
              onClick={() => cancelarEdicao(itemId, campo)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )
    }

    return (
      <div
        className="flex items-center justify-between group cursor-pointer hover:bg-blue-50 p-2 rounded transition-colors"
        onClick={() => iniciarEdicao(itemId, campo)}
      >
        <span className={campo.includes('dcomp') ? 'font-mono text-xs' : ''}>{formatarValor(valor)}</span>
        <Edit3 className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    )
  }

  return (
    <TooltipProvider>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <TrendingUp className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Controle de Créditos</h1>
          </div>
        </div>

        {/* Controles */}
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar período ou DCOMP..."
              className="pl-8"
              value={busca}
              onChange={e => setBusca(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Exportar
            </Button>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Novo Período
            </Button>
          </div>
        </div>

        {/* Tabela Limpa */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-center font-semibold px-4">Período Apurado</TableHead>
                    <TableHead className="text-center font-semibold px-4">PIS Original</TableHead>
                    <TableHead className="text-center font-semibold px-4 bg-blue-50">PIS Pago</TableHead>
                    <TableHead className="text-center font-semibold px-4 bg-blue-50">PIS Saldo</TableHead>
                    <TableHead className="text-center font-semibold px-4 bg-blue-50">DCOMP PIS</TableHead>
                    <TableHead className="text-center font-semibold px-4">COFINS Original</TableHead>
                    <TableHead className="text-center font-semibold px-4 bg-purple-50">COFINS Pago</TableHead>
                    <TableHead className="text-center font-semibold px-4 bg-purple-50">COFINS Saldo</TableHead>
                    <TableHead className="text-center font-semibold px-4 bg-purple-50">DCOMP COFINS</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {dadosPaginados.length > 0 ? (
                    dadosPaginados.map(item => {
                      return (
                        <TableRow key={item.id} className="hover:bg-gray-50">
                          <TableCell className="font-semibold text-blue-600 flex items-center gap-2">
                            {item.periodoApurado}
                          </TableCell>
                          <TableCell className="text-right">{formatMoeda(item.credPisOriginal)}</TableCell>
                          <TableCell className="bg-blue-50">
                            <CelulaEditavel
                              itemId={item.id}
                              campo="credPisPago"
                              valor={item.credPisPago}
                              tipo="number"
                              formatarValor={formatMoeda}
                            />
                          </TableCell>
                          <TableCell className="bg-blue-50">
                            <CelulaEditavel
                              itemId={item.id}
                              campo="credPisSaldo"
                              valor={item.credPisSaldo}
                              tipo="number"
                              formatarValor={formatMoeda}
                            />
                          </TableCell>

                          <TableCell className="bg-blue-50">
                            <CelulaEditavel itemId={item.id} campo="dcompInicialPis" valor={item.dcompInicialPis} />
                          </TableCell>
                          <TableCell className="text-right">{formatMoeda(item.credCofinsOriginal)}</TableCell>

                          <TableCell className="bg-purple-50">
                            <CelulaEditavel
                              itemId={item.id}
                              campo="credCofinsPago"
                              valor={item.credCofinsPago}
                              tipo="number"
                              formatarValor={formatMoeda}
                            />
                          </TableCell>
                          <TableCell className="bg-purple-50">
                            <CelulaEditavel
                              itemId={item.id}
                              campo="credCofinsSaldo"
                              valor={item.credCofinsSaldo}
                              tipo="number"
                              formatarValor={formatMoeda}
                            />
                          </TableCell>

                          <TableCell className="bg-purple-50">
                            <CelulaEditavel
                              itemId={item.id}
                              campo="dcompInicialCofins"
                              valor={item.dcompInicialCofins}
                            />
                          </TableCell>
                        </TableRow>
                      )
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} className="h-24 text-center">
                        <div className="text-gray-400 mb-2">
                          <Search className="h-8 w-8 mx-auto" />
                        </div>
                        <div className="text-gray-600">Nenhum resultado encontrado</div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Paginação */}
        {totalPaginas > 1 && (
          <div className="flex items-center justify-center space-x-4 py-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPaginaAtual(prev => Math.max(prev - 1, 1))}
              disabled={paginaAtual === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Anterior
            </Button>

            <div className="flex items-center gap-2">
              {Array.from({ length: totalPaginas }, (_, i) => i + 1).map(pagina => (
                <Button
                  key={pagina}
                  variant={paginaAtual === pagina ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPaginaAtual(pagina)}
                  className="w-10"
                >
                  {pagina}
                </Button>
              ))}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setPaginaAtual(prev => Math.min(prev + 1, totalPaginas))}
              disabled={paginaAtual === totalPaginas}
            >
              Próxima
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </TooltipProvider>
  )
}
